import { z } from "zod";

import { FieldMappingSourceType, MsdynamicsEntityType } from "./dynamics";

/**
 * Contracts of the two mapping-verification endpoints. They live beside `dynamics.ts`
 * rather than in it because that file is at the edge of the 500-line cap.
 */

// ── Dry run: resolve the mappings against a real prospect, no Dataverse write ──

export const MsdynamicsFieldMappingPreviewRequestSchema = z.object({
	prospectId: z.number(),
	entityType: z.nativeEnum(MsdynamicsEntityType),
});

export type MsdynamicsFieldMappingPreviewRequest = z.infer<
	typeof MsdynamicsFieldMappingPreviewRequestSchema
>;

export const MsdynamicsFieldMappingPreviewRowSchema = z.object({
	mappingId: z.number(),
	sourceType: z.nativeEnum(FieldMappingSourceType),
	sourceField: z.string(),
	msdynamicsField: z.string(),
	msdynamicsFieldLabel: z.string().optional(),
	isActive: z.boolean(),
	value: z.string().nullable(),
	resolved: z.boolean(),
	isAiMapping: z.boolean(),
	/** Why Dataverse would refuse the value — from the sync's own type-coercion pass. */
	skipReason: z.string().nullish(),
	/** Why an empty row is empty when the lead has something behind it (PREC-4752). */
	emptyReason: z.string().nullish(),
});

export type MsdynamicsFieldMappingPreviewRow = z.infer<
	typeof MsdynamicsFieldMappingPreviewRowSchema
>;

export const MsdynamicsFieldMappingPreviewResponseSchema = z.object({
	rows: z.array(MsdynamicsFieldMappingPreviewRowSchema),
	fallbacks: z.array(z.object({ msdynamicsField: z.string(), value: z.string() })),
	missingRequired: z.array(z.string()),
});

export type MsdynamicsFieldMappingPreviewResponse = z.infer<
	typeof MsdynamicsFieldMappingPreviewResponseSchema
>;

// ── Test sync: the real write, per entity tab ─────────────────────────

export const MsdynamicsTestSyncRequestSchema = z.object({
	prospectId: z.number(),
	entityType: z.nativeEnum(MsdynamicsEntityType).optional(),
});

export type MsdynamicsTestSyncRequest = z.infer<typeof MsdynamicsTestSyncRequestSchema>;

export const MsdynamicsTestSyncResponseSchema = z.object({
	success: z.boolean(),
	error: z.string().nullish(),
	msdynamicsContactId: z.string().optional(),
	msdynamicsAccountId: z.string().optional(),
	msdynamicsLeadId: z.string().optional(),
	msdynamicsOpportunityId: z.string().optional(),
	recordUrl: z.string().nullish(),
});

export type MsdynamicsTestSyncResponse = z.infer<typeof MsdynamicsTestSyncResponseSchema>;
